import React, {
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Alert } from "@/components/Alert";
import { Icon } from "@/components/Icon";
import {
    getFileViewerMode,
    shouldUseNativePdfViewer,
    canHideNativePdfDownload,
    type FileViewerMode,
} from "@/utils/fileViewerTypes";
import { extractApiErrorMessage } from "@/utils/apiError";
import { formatAttachmentMeta, cleanHtmlContent } from "@/utils/format";

const FileViewerPdfReact = lazy(async () => {
    const module = await import("@/components/FileViewerPdfReact");
    return { default: module.FileViewerPdfReact };
});

export interface FileViewerProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    filename: string;
    mimeType?: string;
    fileSize?: number;
    fileDate?: string | Date | number;
    loadBlob?: () => Promise<Blob>;
    directUrl?: string;
    allowDownload?: boolean;
    zIndex?: number;
    headerActions?: React.ReactNode;
    bodyTopContent?: React.ReactNode;
}

const MAX_TEXT_PREVIEW_CHARS = 500_000;

export const FileViewer: React.FC<FileViewerProps> = ({
    isOpen,
    onClose,
    title,
    filename,
    mimeType = "",
    fileSize,
    fileDate,
    loadBlob,
    directUrl,
    allowDownload = true,
    zIndex = 110000,
    headerActions,
    bodyTopContent,
}) => {
    const { t } = useTranslation("global");
    const viewerRef = useRef<HTMLDivElement>(null);
    const [nativePdfFailed, setNativePdfFailed] = useState<boolean>(false);
    const platformSupportsNativePdf = shouldUseNativePdfViewer(allowDownload);
    const useNativePdf = platformSupportsNativePdf && !nativePdfFailed;

    const [mode, setMode] = useState<FileViewerMode>("unsupported");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [blob, setBlob] = useState<Blob | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [textContent, setTextContent] = useState<string>("");
    const [silentDownload, setSilentDownload] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [iframeLoading, setIframeLoading] = useState<boolean>(false);

    const resetState = useCallback((): void => {
        setLoading(false);
        setError(null);
        setBlob(null);
        setTextContent("");
        setPdfData(null);
        setSilentDownload(false);
        setMode("unsupported");
        setIsFullscreen(false);
        setIframeLoading(false);
        setNativePdfFailed(false);
        setBlobUrl((current) => {
            if (current) {
                URL.revokeObjectURL(current);
            }
            return null;
        });
    }, []);

    const handleClose = useCallback((): void => {
        resetState();
        onClose();
    }, [onClose, resetState]);

    const handleDownload = useCallback((): void => {
        if (blob) {
            saveAs(blob, filename);
        } else if (directUrl) {
            window.open(directUrl, "_blank");
        }
    }, [blob, filename, directUrl]);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, resetState]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const nextMode = getFileViewerMode(filename, mimeType);
        setMode(nextMode);
        setLoading(true);
        setError(null);

        if (directUrl) {
            setBlobUrl(directUrl);
            setLoading(false);
            return;
        }

        if (!loadBlob) {
            setError("No load function provided");
            setLoading(false);
            return;
        }

        if (nextMode === "unsupported" && allowDownload) {
            setSilentDownload(true);

            loadBlob()
                .then((loadedBlob) => {
                    saveAs(loadedBlob, filename);
                    handleClose();
                })
                .catch(async (err: unknown) => {
                    setSilentDownload(false);
                    setError(await extractApiErrorMessage(err));
                    setLoading(false);
                });

            return;
        }

        loadBlob()
            .then(async (loadedBlob) => {
                setBlob(loadedBlob);
                const url = URL.createObjectURL(loadedBlob);
                setBlobUrl(url);

                if (nextMode === "pdf") {
                    try {
                        const buffer = await loadedBlob.arrayBuffer();
                        setPdfData(new Uint8Array(buffer));
                    } catch {
                        // ignore arrayBuffer conversion error; blobUrl will be used
                    }
                }

                if (nextMode === "html") {
                    try {
                        const rawText = await loadedBlob.text();
                        const cleaned = cleanHtmlContent(rawText);
                        if (cleaned && cleaned !== rawText) {
                            const sanitizedBlob = new Blob([cleaned], {
                                type: "text/html;charset=utf-8",
                            });
                            setBlob(sanitizedBlob);
                            const cleanUrl = URL.createObjectURL(sanitizedBlob);
                            setBlobUrl(cleanUrl);
                        }
                    } catch {
                        // ignore clean error, original blobUrl remains
                    }
                    setIframeLoading(true);
                }

                if (nextMode === "text") {
                    const rawText = await loadedBlob.text();
                    setTextContent(
                        rawText.length > MAX_TEXT_PREVIEW_CHARS
                            ? `${rawText.slice(0, MAX_TEXT_PREVIEW_CHARS)}\n\n…`
                            : rawText
                    );
                }
            })
            .catch(async (err: unknown) => {
                setError(await extractApiErrorMessage(err));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [
        allowDownload,
        filename,
        handleClose,
        isOpen,
        loadBlob,
        mimeType,
        t,
        useNativePdf,
    ]);

    if (!isOpen || silentDownload) {
        return null;
    }

    const isErrorView = Boolean(error);
    const showDownloadButton =
        allowDownload &&
        mode !== "unsupported" &&
        !isErrorView &&
        mode !== "html";
    const subtitle = isErrorView
        ? undefined
        : formatAttachmentMeta(fileDate, fileSize) || undefined;
    const modalMaxWidth = isErrorView ? "md" : "4xl";
    const isPdfView = mode === "pdf" && !loading && !isErrorView;
    const isNativePdfView = isPdfView && useNativePdf;

    const contentClassName = isErrorView
        ? ""
        : loading
          ? ["pdf", "html"].includes(mode)
              ? "h-[45vh] min-h-[320px]"
              : ""
          : ["pdf", "html"].includes(mode)
            ? isFullscreen
                ? "h-full min-h-0"
                : "h-[90vh]"
            : "";

    const bodyClassName = isErrorView
        ? "p-6"
        : loading
          ? "flex flex-1 flex-col items-center justify-center p-6 h-full"
          : isNativePdfView || mode === "html"
            ? "overflow-hidden flex flex-col p-1"
            : mode === "pdf"
              ? "overflow-auto p-2"
              : "";

    const footer = (
        <>
            <button
                type="button"
                onClick={handleClose}
                className="btn secondary flex-1 min-w-[120px] max-w-[180px] justify-center"
            >
                {t("modal.close")}
            </button>
            {mode === "html" && blobUrl && !isErrorView && (
                <button
                    type="button"
                    onClick={() => window.open(blobUrl, "_blank")}
                    className="btn primary flex-1 min-w-[120px] max-w-[180px] justify-center"
                    title={t("file_viewer.open_new_tab")}
                >
                    <Icon value="externalLink" size={14} />
                    <span>{t("file_viewer.open_new_tab")}</span>
                </button>
            )}
            {showDownloadButton && (
                <button
                    type="button"
                    onClick={handleDownload}
                    disabled={!blob || loading}
                    className="btn primary flex-1 min-w-[120px] max-w-[180px] justify-center"
                >
                    <Icon value="download" size={14} />
                    <span>{t("developments.detail.download_file")}</span>
                </button>
            )}
        </>
    );

    const renderPdfViewer = (): React.ReactNode => {
        if (!blobUrl) {
            return null;
        }

        if (useNativePdf) {
            const pdfFragment = "#view=FitH";

            return (
                <div
                    ref={viewerRef}
                    className="size-full min-h-0 overflow-hidden rounded-xl"
                >
                    <iframe
                        title={title}
                        src={`${blobUrl}${pdfFragment}`}
                        className="size-full border-0 bg-white"
                        onError={() => setNativePdfFailed(true)}
                    />
                </div>
            );
        }

        if (!blobUrl && !pdfData) {
            return (
                <div className="flex min-h-[320px] items-center justify-center text-slate-500">
                    <Icon value="loader" size={22} className="animate-spin" />
                </div>
            );
        }

        return (
            <Suspense
                fallback={
                    <div className="flex min-h-[320px] items-center justify-center text-slate-500">
                        <Icon
                            value="loader"
                            size={22}
                            className="animate-spin"
                        />
                    </div>
                }
            >
                <FileViewerPdfReact
                    fileUrl={blobUrl}
                    fileData={pdfData}
                    onError={(message) =>
                        setError(message || t("file_viewer.load_error"))
                    }
                />
            </Suspense>
        );
    };

    const renderBody = (): React.ReactNode => {
        if (loading) {
            return (
                <div className="flex flex-1 size-full min-h-[220px] flex-col items-center justify-center text-slate-500 dark:text-slate-400 gap-3 py-8">
                    <Icon
                        value="loader"
                        size={28}
                        className="animate-spin text-primary"
                    />
                    <span className="text-sm font-medium">
                        {t("file_viewer.loading")}
                    </span>
                </div>
            );
        }

        if (error) {
            const displayError =
                error.includes("postMessage") ||
                error.includes("ArrayBuffer") ||
                error.includes("Worker")
                    ? t("file_viewer.load_error")
                    : error;

            return (
                <div className="space-y-4 py-2">
                    <Alert
                        variant="danger"
                        className="text-sm"
                        copyable={false}
                    >
                        {displayError}
                    </Alert>
                    {allowDownload && blob && (
                        <div className="flex justify-center">
                            <button
                                type="button"
                                onClick={handleDownload}
                                className="btn primary flex items-center gap-2 text-xs font-semibold"
                            >
                                <Icon value="download" size={15} />
                                <span>
                                    {t("developments.detail.download_file")}
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        if (mode === "unsupported") {
            return (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600 dark:border-white/10 dark:bg-dark-subtle dark:text-slate-300">
                    {t("file_viewer.preview_not_available_no_download")}
                </div>
            );
        }

        if (!blobUrl) {
            return null;
        }

        if (mode === "pdf") {
            return renderPdfViewer();
        }

        if (mode === "text") {
            return (
                <div
                    ref={viewerRef}
                    className="-mx-2 max-h-[70vh] overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-subtle"
                >
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-slate-800 dark:text-slate-100">
                        {textContent}
                    </pre>
                </div>
            );
        }

        if (mode === "html") {
            return (
                <div
                    ref={viewerRef}
                    className="size-full min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 relative"
                >
                    {iframeLoading && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm">
                            <Icon
                                value="loader"
                                size={32}
                                className="animate-spin text-primary"
                            />
                            <span className="text-xs font-medium text-slate-500 dark:text-zinc-400">
                                {t("file_viewer.loading")}
                            </span>
                        </div>
                    )}
                    <iframe
                        title={title}
                        src={blobUrl}
                        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
                        className="size-full border-0 bg-white"
                        onLoad={() => setIframeLoading(false)}
                    />
                </div>
            );
        }

        return (
            <div
                ref={viewerRef}
                className="-mx-2 flex min-h-[320px] items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-subtle"
            >
                <img
                    src={blobUrl}
                    alt={title}
                    className="max-h-[70vh] max-w-full object-contain"
                />
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            subtitle={subtitle}
            maxWidth={modalMaxWidth}
            contentClassName={contentClassName}
            bodyClassName={bodyClassName}
            footer={footer}
            isFullscreen={isFullscreen}
            onFullscreenChange={setIsFullscreen}
            hideCancel
            hideFullscreen={!["pdf", "html"].includes(mode) || isErrorView}
            zIndex={zIndex}
            headerActions={headerActions}
        >
            {bodyTopContent && (
                <div className="flex justify-center">{bodyTopContent}</div>
            )}
            {renderBody()}
        </Modal>
    );
};

export default FileViewer;
